/** @mui/material imports */
import Backdrop from '@mui/material/Backdrop/Backdrop';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
/** @mui/icons-material imports */
/** React imports */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/** App imports */
import { appContext } from '../../Contexts/AppContext';

import {
  DrillHoleObject,
  RegisteredImageTypeCount,
  ValidatorProps,
  VisualizerDataSource,
} from '../../TypeScript/interfaces';
import { getDestructuredImageType } from '../../Hooks/useRegisteredImageTypes';
import {
  DATA_SOURCE_FIELD_DELIMITER,
  DATA_SOURCE_KEY_DELIMITER,
  DEFAULT_ZOOM,
  VIS_SEARCH_PARAM_NAMES,
  VIS_SEARCH_PARAM_SOURCES,
  X_RANGE_FIELD_DELIMITER,
} from '../constants';
import {
  LogStatus,
  LogType,
  RegisteredImageType,
  VisualizerDataSourceTypes,
} from '../../TypeScript/enums';
import { DrillholeContext } from '../../Contexts/DrillholeContext';
import { SchemaLogType } from '@goldspotdiscoveries/atoms/lib/shared/interfaces';
import {
  ManualXRangeConfig,
  ManualXRangesValues,
} from '../../VisualCanvas/src/utils/xRange/useManualXRange';
import { DataRange } from '@goldspotdiscoveries/types';
import { useAppAlert } from '../../Contexts/AppAlertContext';

export const NewVisualizerQueryParamsContext = createContext<{
  queryMultipleDrillholes: boolean;
  querySelectedDrillholes: DrillHoleObject[];
  querySelectedDataSources: VisualizerDataSource[];
  queryZoom?: number;
  queryXRanges?: ManualXRangesValues;
  updateSearchParams: (
    selectedDataSources: VisualizerDataSource[],
    selectedDrillholes: DrillHoleObject[],
    isMultipleDrillholeMode: boolean,
    selectedZoom: number,
    xRangesProperties: ManualXRangesValues,
  ) => void;
}>({
  queryMultipleDrillholes: false,
  querySelectedDrillholes: [],
  querySelectedDataSources: [],
  updateSearchParams: (
    selectedDataSources: VisualizerDataSource[],
    selectedDrillholes: DrillHoleObject[],
    isMultipleDrillholeMode: boolean,
    selectedZoom: number,
    xRangesProperties: ManualXRangesValues,
  ): void => {},
});

/**
 * Validate the drillhole id in the path and provide context
 *
 * Note: Assumes project id is validated
 *
 * @param props standard validator props
 * @returns child component with validation applied
 */
const NewVisualizerQueryParamsValidator: React.FC<ValidatorProps> = (
  props: React.PropsWithChildren<ValidatorProps>,
): JSX.Element => {
  const { t } = useTranslation();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { activeProject } = useContext(appContext);
  const { addAppAlert } = useAppAlert();
  const { drillholesMap, drillholesMapReady } = useContext(DrillholeContext);
  const [validatedDrillholes, setValidatedDrillholes] =
    useState<DrillHoleObject[]>();
  const [validatedDataSources, setValidatedDataSources] =
    useState<VisualizerDataSource[]>();
  const [multipleDrillholes, setMultipleDrillholes] = useState<boolean>();
  const [validatedZoom, setValidatedZoom] = useState<number | undefined>();
  const [customXRange, setCustomXRange] = useState<ManualXRangesValues>();

  // When drillhole context is ready, begin validation
  useEffect(() => {
    if (drillholesMapReady) {
      const drillholes = urlSearchParams.getAll(
        VIS_SEARCH_PARAM_NAMES.DRILLHOLE,
      );
      const sources = urlSearchParams.getAll(VIS_SEARCH_PARAM_NAMES.SOURCE);
      const multiple =
        urlSearchParams.get(VIS_SEARCH_PARAM_NAMES.MULTIPLE) ?? '';
      const zoom = urlSearchParams.get(VIS_SEARCH_PARAM_NAMES.ZOOM) ?? '';

      const xRanges = urlSearchParams.getAll(VIS_SEARCH_PARAM_NAMES.X_RANGE);
      const globalXRange =
        urlSearchParams.get(VIS_SEARCH_PARAM_NAMES.GLOBAL_RANGE) ?? '';

      doValidation(drillholes, sources, multiple, zoom, xRanges, globalXRange);
    }
  }, [drillholesMapReady, urlSearchParams, activeProject]);

  /**
   * Validate a list of drillhole ids or names
   * @param drillholeIds an array of drillhole id strings or drillhole names
   * @param multipleDrillholes true if multiple drillhole mode is selected
   * @returns a list of litholens drillhole ids
   */
  const getValidatedDrillholes = useCallback(
    (
      drillholeIds: string[] = [],
      multipleDrillholes: boolean,
    ): DrillHoleObject[] => {
      const validatedDrillholes: DrillHoleObject[] = [];
      const invalidDrillholes: string[] = [];
      // if multiple drillholes mode is NOT selected, only use the first drillhole
      const qtyDrillholesToValidate: number = multipleDrillholes
        ? drillholeIds.length
        : 1;
      for (let i = 0; i < qtyDrillholesToValidate; i++) {
        const drillholeIdString: string = drillholeIds[i];
        const drillholeId: number = Number(drillholeIdString);
        // get litholens drillhole by name or id
        const drillhole: DrillHoleObject | undefined = isNaN(drillholeId)
          ? drillholesMap.byName[drillholeIdString]
          : !!drillholesMap.byId[drillholeId]
            ? drillholesMap.byId[drillholeId]
            : drillholesMap.byId[drillholeIdString];
        if (drillhole) {
          validatedDrillholes.push(drillhole);
        } else if (drillholeIdString) {
          invalidDrillholes.push(drillholeIdString);
        }
      }
      if (invalidDrillholes.length > 0) {
        addAppAlert({
          severity: 'warning',
          message: t('ALERT_WARNING_VISUALIZER_QUERY_INVALID_DRILLHOLES'),
          details: t('ALERT_DETAILS_VISUALIZER_QUERY_INVALID_DRILLHOLES', {
            drillholes: invalidDrillholes.join(', '),
          }),
        });
      }
      return validatedDrillholes;
    },
    [drillholesMap],
  );

  /**
   * Validate a list of data sources to visualize
   *
   * Note that we will not render non-valid url content to avoid risks of XSS or other security risks (e.g. imagine the "invalid" data source was "Call this phone number immediately: 1-800-SCAMMER")
   */
  const getValidatedDataSources = useCallback(
    (
      sources: string[] = [],
      multipleDrillholes: boolean,
    ): VisualizerDataSource[] => {
      const validatedDataSources: VisualizerDataSource[] = [];
      const qtyDataSourcesToValidate: number = multipleDrillholes
        ? 1
        : sources.length;
      let hasInvalidDataSources: boolean = false;
      for (let i = 0; i < qtyDataSourcesToValidate; i++) {
        if (sources[i]) {
          const decodedDataSource: VisualizerDataSource = decodeDataSourceKey(
            sources[i],
          );

          switch (decodedDataSource.type) {
            case VisualizerDataSourceTypes.shape:
            case VisualizerDataSourceTypes.image:
              {
                const { imageType, wavelength } = decodedDataSource.properties;
                const valid: boolean = getIsImageTypeValid(
                  imageType as RegisteredImageType,
                  wavelength,
                );
                if (valid) {
                  validatedDataSources.push(decodedDataSource);
                } else {
                  hasInvalidDataSources = true;
                }
              }
              break;
            case VisualizerDataSourceTypes.log:
              {
                const { id, dataItem } = decodedDataSource.properties;
                const valid: boolean = !!id && !!dataItem;
                if (valid) {
                  validatedDataSources.push(decodedDataSource);
                } else {
                  hasInvalidDataSources = true;
                }
              }
              break;
            default:
              hasInvalidDataSources = true;
          }
        }
      }
      if (hasInvalidDataSources) {
        addAppAlert({
          severity: 'warning',
          message: t('ALERT_WARNING_VISUALIZER_QUERY_INVALID_DATASOURCES'),
        });
      }
      return validatedDataSources;
    },
    [activeProject],
  );

  const getIsImageTypeValid = (
    imageType: RegisteredImageType,
    wavelength?: string,
  ): boolean => {
    if (activeProject.litholensData?.imagesTypeCount) {
      return !!activeProject.litholensData.imagesTypeCount.find(
        (value: RegisteredImageTypeCount) =>
          value.image_type === imageType && value.wavelength == wavelength,
      );
    } else {
      return false;
    }
  };

  /**
   * Get a valid zoom value
   * @param zoom zoom value from query params
   * @returns a qualified zoom value between 0.1 and 200, rounded to the nearest tenth, falls back to {@link DEFAULT_ZOOM}
   */
  const getValidatedZoom = (zoom?: string): number | undefined => {
    if (zoom) {
      const notValidatedZoom: number = Number(zoom);
      if (!isNaN(notValidatedZoom)) {
        return Math.round(notValidatedZoom * 10) / 10;
      } else {
        return DEFAULT_ZOOM;
      }
    } else {
      return undefined;
    }
  };

  const getValidatedXRange = (
    xRange: string[],
    globalRange: string,
  ): ManualXRangesValues => {
    const xRangesMap = new Map<string, ManualXRangeConfig>();
    const errors: (string | null)[] = [];

    xRange.forEach((element) => {
      const [key, values] = element.split(X_RANGE_FIELD_DELIMITER);

      if (values === 'global') {
        // This value follows global value
        xRangesMap.set(key, { global: true });
        return;
      }

      if (key == null || key.length < 1) {
        errors.push(null);
        return;
      }

      if (values == null) {
        errors.push(key);
        return;
      }

      const [min, max] = values.split(',');

      const parsedMin = Number(min);
      const parsedMax = Number(max);

      if (isNaN(parsedMin) || isNaN(parsedMax)) {
        errors.push(key);
        return;
      }

      xRangesMap.set(key, {
        global: false,
        local: { min: parsedMin, max: parsedMax },
      });
    });

    let validatedGlobal: DataRange | undefined = undefined;

    if (globalRange != null) {
      const [min, max] = globalRange.split(',');

      const parsedMin = Number(min);
      const parsedMax = Number(max);

      if (isNaN(parsedMin) || isNaN(parsedMax)) {
        // something didn't pass
      } else {
        validatedGlobal = {
          min: parsedMin,
          max: parsedMax,
        };
      }
    }

    return {
      manualXRanges: xRangesMap,
      globalRange: validatedGlobal,
    };
  };

  /**
   * Parse and validate the query params and set them in state so that they are available to the child component
   * @param drillholes the string value of drillholes in `&drillholes=2100,2101,2012` or `&drillholes=2100`
   * @param reports the string value of reports in `&reports=[TO DO]`
   * @param image_types the string value of image_types in `&image_types=core|null,dry|null,hs|20` or `&image_types=core|null`
   * @param multiple the string value of multiple in `&multiple=true`
   */
  const doValidation = useCallback(
    (
      drillholes: string[],
      dataSources: string[],
      multiple: string,
      zoom: string,
      xRanges: string[],
      globalRange: string,
    ): void => {
      if (drillholesMapReady) {
        // determine if multiple drillhole mode should be set (if explicitly true, or implied by passing multiple drillholes)
        const multipleDrillholes: boolean =
          multiple === 'true' ||
          (multiple !== 'false' && drillholes != null && drillholes.length > 1);
        // validate query params
        const validatedDrillholes: DrillHoleObject[] = getValidatedDrillholes(
          drillholes,
          multipleDrillholes,
        );
        const validatedDataSources: VisualizerDataSource[] =
          getValidatedDataSources(dataSources, multipleDrillholes);
        const validatedZoom: number | undefined = getValidatedZoom(zoom);

        const validatedXRange = getValidatedXRange(xRanges, globalRange);

        // set validated values in state
        setValidatedDrillholes(validatedDrillholes);
        setValidatedDataSources(validatedDataSources);
        setMultipleDrillholes(multipleDrillholes);
        setValidatedZoom(validatedZoom);
        setCustomXRange(validatedXRange);
      }
    },
    [
      drillholesMapReady,
      getValidatedDrillholes,
      getValidatedDataSources,
      getValidatedZoom,
      getValidatedXRange,
    ],
  );

  /**
   *
   * @param dataSourceKey e.g. 'logs_api_::log.2024.09.11.15.23.12.496.670::first_dom'
   * @returns
   */
  const decodeDataSourceKey = (dataSourceKey: string): VisualizerDataSource => {
    const [dataSourceType, prop1, prop2, prop3, prop4] = dataSourceKey.split(
      DATA_SOURCE_KEY_DELIMITER,
    );

    switch (dataSourceType) {
      case VIS_SEARCH_PARAM_SOURCES.IMAGE_STRIPLOG: {
        const { image_type, wavelength } = getDestructuredImageType(prop1);
        return {
          type: VisualizerDataSourceTypes.image,
          key: dataSourceKey,
          label: [
            `LABEL_DATA_SOURCE.IMAGES.${image_type.toUpperCase()}`,
            {
              imageType: image_type,
              wavelength: wavelength,
            },
          ],
          properties: { imageType: image_type, wavelength },
        };
      }
      case VIS_SEARCH_PARAM_SOURCES.SHAPE_IMAGE_LOG: {
        let models = undefined;

        try {
          models = JSON.parse(prop4);
        } catch (error) {
          // This is fine if there is no models
        }

        return {
          type: VisualizerDataSourceTypes.shape,
          key: [dataSourceType, prop1, prop2].join(DATA_SOURCE_KEY_DELIMITER),
          label: ['LABEL_DATA_SOURCE.SHAPE', { imageType: prop1 }],
          properties: {
            imageType: prop1,
            wavelength: prop2 === 'null' ? undefined : prop2,
            models: models,
          },
        };
      }
      case VIS_SEARCH_PARAM_SOURCES.LOGS_API_LOG:
      default:
        // fallback case; TO DO: error handling and better fallback case
        const timestamp: number = new Date().valueOf();
        let [logType, description, schemaType, modelTrainId] = prop3.split(
          DATA_SOURCE_FIELD_DELIMITER,
        );

        if (!logType || logType === 'null') {
          // Specifically checking for empty string or null
          logType = LogType.default;
        }

        const modelTrainAsNumber = Number(modelTrainId);

        return {
          type: VisualizerDataSourceTypes.log,
          key: [dataSourceType, prop1, prop2].join(DATA_SOURCE_KEY_DELIMITER),
          label: [
            'LABEL_DATA_SOURCE.LOGS',
            {
              description: description ?? '',
              dataItem: prop2,
            },
          ],
          properties: {
            id: prop1,
            dataItem: prop2,
            timestamp,
            description,
            logType: logType as LogType,
            status: LogStatus.locked,
            schemaType: schemaType as SchemaLogType | undefined,
            modelTrainId: isNaN(modelTrainAsNumber)
              ? undefined
              : modelTrainAsNumber,
          },
        };
    }
  };

  const updateSearchParams = (
    selectedDataSources: VisualizerDataSource[],
    selectedDrillholes: DrillHoleObject[],
    isMultipleDrillholeMode: boolean,
    selectedZoom: number,
    xRangesProperties: ManualXRangesValues,
  ): void => {
    const updatedSearch = new URLSearchParams();

    for (const source of selectedDataSources) {
      if (source.type === VisualizerDataSourceTypes.file) {
        continue;
      }

      let fields = null;

      switch (source.type) {
        case VisualizerDataSourceTypes.log:
          fields = [
            source.properties.logType,
            source.properties.description,
            source.properties.schemaType,
            source.properties.modelTrainId,
          ].join(DATA_SOURCE_FIELD_DELIMITER);
          break;
        case VisualizerDataSourceTypes.shape:
          fields = JSON.stringify(source.properties.models);
          break;
        default:
        // Do nothing
      }

      const entry = `${source.key}${fields != null ? DATA_SOURCE_KEY_DELIMITER + fields : ''}`;

      updatedSearch.append(VIS_SEARCH_PARAM_NAMES.SOURCE, entry);
    }

    for (const hole of selectedDrillholes) {
      updatedSearch.append(VIS_SEARCH_PARAM_NAMES.DRILLHOLE, `${hole.id}`);
    }

    updatedSearch.set(
      VIS_SEARCH_PARAM_NAMES.MULTIPLE,
      `${isMultipleDrillholeMode}`,
    );
    updatedSearch.set(VIS_SEARCH_PARAM_NAMES.ZOOM, `${selectedZoom}`);

    Array.from(xRangesProperties.manualXRanges.entries()).forEach(
      ([key, value]) => {
        if (value.global) {
          updatedSearch.append(
            VIS_SEARCH_PARAM_NAMES.X_RANGE,
            [key, 'global'].join(X_RANGE_FIELD_DELIMITER),
          );
        } else if (value.local != null) {
          updatedSearch.append(
            VIS_SEARCH_PARAM_NAMES.X_RANGE,
            [key, `${value.local.min},${value.local.max}`].join(
              X_RANGE_FIELD_DELIMITER,
            ),
          );
        }
      },
    );

    if (xRangesProperties.globalRange != null) {
      updatedSearch.set(
        VIS_SEARCH_PARAM_NAMES.GLOBAL_RANGE,
        [
          xRangesProperties.globalRange.min,
          xRangesProperties.globalRange.max,
        ].join(','),
      );
    }

    setUrlSearchParams(updatedSearch, { replace: true });
  };

  /**
   * Return child component if validation states have all been set
   *
   * If state has not been set, value will be undefined; all states are initialized as undefined
   */
  return (
    <>
      {!!validatedDrillholes &&
      !!validatedDataSources &&
      multipleDrillholes !== undefined &&
      customXRange != null ? (
        <NewVisualizerQueryParamsContext.Provider
          value={{
            queryMultipleDrillholes: multipleDrillholes,
            querySelectedDrillholes: validatedDrillholes,
            querySelectedDataSources: validatedDataSources,
            queryZoom: validatedZoom,
            queryXRanges: customXRange,
            updateSearchParams,
          }}
        >
          {props.children}
        </NewVisualizerQueryParamsContext.Provider>
      ) : (
        <Backdrop sx={{ color: '#fff', zIndex: '2000' }} open={true}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </>
  );
};

export default NewVisualizerQueryParamsValidator;
